/**
 * Opening creation logic
 * Refactored for readability (single file)
 */

import { IAccessoryPosition, IBotData } from '../../../models';
import {
  DEFAULT_COEFFICIENTS,
  IBotAccessory,
  SectionCreationContext,
} from '../../../models/bot';
import { dispatchMutation } from '../../../utils/dispatch.utils';
import { ahuApi, openingApi } from '../../../services';
import { calculateExtraClearances, calculateInitialSize, MIN_CLEAR_MAP, normalizeEven, resolveAirflow, roundBasedOnArguments } from '../designAHU.utils';

/* ------------------------------------------------------------------ */
/* Main logic                                                         */
/* ------------------------------------------------------------------ */

export async function createOpening(
  botSection: any,
  apiSection: any,
  accessory: IBotAccessory,
  context: SectionCreationContext,
  botData: IBotData,
): Promise<void> {
  const { getOptions, onProgress, checkStopped } = context;
  if (checkStopped?.()) return;

  const mounting_plane_id = accessory.mounting_plane_id ?? 3;
  const isFloorOrRoof = mounting_plane_id !== 3 && mounting_plane_id !== 4;

  const { airflow, air_stream_id } = resolveAirflow(
    accessory,
    botSection,
    botData,
  );

  let { width, height } = calculateInitialSize(airflow);

  /* ---------------- Create opening ---------------- */

  let opening: any = await dispatchMutation(
    openingApi.endpoints.createOpening,
    {
      body: {
        section_id: apiSection.id,
        airflow_actual: airflow,
        width,
        height,
        air_stream_id,
      },
    },
    getOptions(),
  );
  if (checkStopped?.()) return;

  /* ---------------- Clearances ---------------- */

  const { extra_width, extra_height, extra_length } =
    calculateExtraClearances(
      opening,
      mounting_plane_id,
      isFloorOrRoof,
    );

  /* ---------------- Clamp to section ---------------- */

  if (width + extra_width > apiSection.aw_width) {
    width = Math.max(apiSection.aw_width - extra_width, 6);
  }

  if (!isFloorOrRoof && height + extra_height > apiSection.aw_height) {
    height = Math.max(apiSection.aw_height - extra_height, 6);
  }

  width = normalizeEven(Math.floor(width));
  height = normalizeEven(Math.floor(height));

  /* ---------------- Update opening if needed ---------------- */

  if (opening.width !== width || opening.height !== height) {
    let airflow_actual = opening.airflow_actual;
    const velocity = (airflow_actual * 144) / (width * height);

    if (velocity > 2000) {
      airflow_actual = roundBasedOnArguments(
        (width * height * 1000) / 144,
        100,
      );
    }

    opening = await dispatchMutation(
      openingApi.endpoints.updateOpening,
      {
        id: opening.id,
        body: { width, height, airflow_actual },
      },
      getOptions(),
    );
  }

  if (checkStopped?.()) return;

  /* ---------------- Position ---------------- */

  const pos: IAccessoryPosition = await dispatchMutation(
    ahuApi.endpoints.updateAccessoryPosition,
    {
      id: opening.id,
      body: {
        mounting_plane_id,
        default_pos_x: !isFloorOrRoof,
      },
    },
    getOptions(),
  );

  if (!pos?.default_pos_x && isFloorOrRoof) {
    const clears = MIN_CLEAR_MAP[String(mounting_plane_id)];
    if (clears) {
      const x = Math.max(
        opening[clears.ups_down] || 0,
        opening[clears.ups] || 0,
      );

      await dispatchMutation(
        ahuApi.endpoints.updateAccessoryPosition,
        {
          id: opening.id,
          body: { x },
        },
        getOptions(),
      );
    }
  }

  if (checkStopped?.()) return;

  /* ---------------- Section length ---------------- */

  if (
    isFloorOrRoof &&
    apiSection.aw_length < opening.height + extra_length
  ) {
    const length = roundBasedOnArguments(
      apiSection.length +
        (opening.height + extra_length - apiSection.aw_length),
      apiSection.increment || 1,
    );

    await dispatchMutation(ahuApi.endpoints.updateSection, {
      id: apiSection.id,
      body: {
        auto_adjust: false,
        length,
      },
    });
  }

  onProgress?.(DEFAULT_COEFFICIENTS.opening);
  accessory.model = opening;
}
